import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import Cookies from 'js-cookie';
import { message } from 'antd';

// 扩展 AxiosRequestConfig 类型，允许 _resolve/_reject 字段
interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  // eslint-disable-next-line no-unused-vars
  _resolve?: (_value: AxiosResponse) => void;
  // eslint-disable-next-line no-unused-vars
  _reject?: (_reason?: any) => void;
}

const instance = axios.create({
  baseURL: process.env.NEXT_APP_API_HOST,
  timeout: 60000,
  headers: {
    tenantId: `${process.env.NEXT_APP_TENANTID}`,
  },
});

// ========== API 重试机制配置 ===========
// 配置 axios-retry：自动重试网络错误和超时
axiosRetry(instance, {
  retries: 3, // 最多重试 3 次
  retryDelay: axiosRetry.exponentialDelay, // 指数退避策略：1s, 2s, 4s
  retryCondition: (error) => {
    // 只重试网络错误（ECONNRESET, ETIMEDOUT, ENOTFOUND等）
    // 不重试业务错误（404, 500等HTTP状态码）
    return axiosRetry.isNetworkOrIdempotentRequestError(error) || error.code === 'ECONNRESET';
  },
  onRetry: (retryCount, error, requestConfig) => {
    // 仅在构建时输出重试日志（避免在生产环境影响用户体验）
    if (typeof window === 'undefined') {
      console.log(
        `[API Retry] Attempt ${retryCount} for ${requestConfig.method?.toUpperCase()} ${requestConfig.url} - Error: ${error.message}`,
      );
    }
  },
});

// 添加重定向标志
let isRedirecting = false;

// ========== 请求合并+结果短暂缓存（主请求结束后500ms内缓存结果） ===========
// 用于存储短时间内的相同请求
const pendingRequestMap = new Map<
  string,
  {
    promise: Promise<AxiosResponse>;
    timestamp: number;
    mergedCount: number; // 合并请求数量
    retried: boolean; // 是否已重试过
  }
>();

// 用于存储主请求返回后缓存结果500ms
const resultCacheMap = new Map<string, { response: AxiosResponse; timer: ReturnType<typeof setTimeout> }>();

// 生成请求唯一 key（方法+url+参数+body）
function getRequestKey(config: AxiosRequestConfig) {
  const { method, url, params, data } = config;
  return [method, url, JSON.stringify(params), typeof data === 'string' ? data : JSON.stringify(data)].join('&');
}

// 请求拦截器：合并 500ms 内的相同请求
instance.interceptors.request.use((config) => {
  const key = getRequestKey(config);
  // 优先查结果缓存
  if (resultCacheMap.has(key)) {
    const cache = resultCacheMap.get(key)!;
    // 直接返回缓存结果
    return Promise.reject({ __CACHED__: true, response: cache.response });
  }
  const pending = pendingRequestMap.get(key);
  if (pending) {
    // 主请求未返回，合并进主 promise
    pending.mergedCount++;
    return Promise.reject({ __MERGED__: true, key });
  }
  // 新请求，创建 promise 并缓存
  let _resolve: CustomAxiosRequestConfig['_resolve'];
  let _reject: CustomAxiosRequestConfig['_reject'];
  const reqPromise = new Promise<AxiosResponse>((resolve, reject) => {
    _resolve = resolve;
    _reject = reject;
  });
  (config as CustomAxiosRequestConfig)._resolve = _resolve;
  (config as CustomAxiosRequestConfig)._reject = _reject;
  pendingRequestMap.set(key, {
    promise: reqPromise,
    timestamp: Date.now(),
    mergedCount: 1, // 第一个请求
    retried: false,
  });
  return config;
});

// 响应拦截器：分发结果给所有等待者
instance.interceptors.response.use(
  (response) => {
    const key = getRequestKey(response.config);
    if (pendingRequestMap.has(key)) {
      (response.config as CustomAxiosRequestConfig)?._resolve?.(response);
      pendingRequestMap.delete(key); // 请求完成立即删除
      // 缓存结果500ms
      const timer = setTimeout(() => {
        resultCacheMap.delete(key);
      }, 500);
      resultCacheMap.set(key, { response, timer });
    }
    return response;
  },
  async (error) => {
    // 合并请求，直接返回主 promise
    if (error && error.__MERGED__ && error.key) {
      const pending = pendingRequestMap.get(error.key);
      if (pending) return pending.promise;
    }
    // 命中缓存，直接返回缓存结果
    if (error && error.__CACHED__ && error.response) {
      return Promise.resolve(error.response);
    }
    // 其它错误
    if (error.config) {
      const key = getRequestKey(error.config);
      const pending = pendingRequestMap.get(key);
      if (pending) {
        // 检查是否需要重试
        if (!pending.retried && pending.mergedCount > 3) {
          pending.retried = true; // 标记已重试
          try {
            // 重新发起主请求，复用 resolve/reject
            const axiosInstance = error.config.__fromRetry ? instance : axios;
            const retryConfig = { ...error.config, __fromRetry: true };
            const retryResponse = await axiosInstance.request(retryConfig);
            (error.config as CustomAxiosRequestConfig)?._resolve?.(retryResponse);
            pendingRequestMap.delete(key);
            // 缓存结果500ms
            const timer = setTimeout(() => {
              resultCacheMap.delete(key);
            }, 500);
            resultCacheMap.set(key, { response: retryResponse, timer });
            return retryResponse;
          } catch (retryErr) {
            (error.config as CustomAxiosRequestConfig)?._reject?.(retryErr);
            pendingRequestMap.delete(key);
            return Promise.reject(retryErr);
          }
        } else {
          (error.config as CustomAxiosRequestConfig)?._reject?.(error);
          pendingRequestMap.delete(key);
        }
      }
    }
    return Promise.reject(error);
  },
);

// ========== 请求合并拦截器 ===========
instance.interceptors.request.use(
  (config) => {
    const token = Cookies.get('userToken');
    // const token = ls_getItem('userToken');
    if ((config as any).__skipMerge) {
      delete (config as any).__skipMerge;
      if (token != undefined) {
        config.headers['Authorization'] = token;
      }
      return config;
    }
    if (token != undefined) {
      config.headers['Authorization'] = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// token 处理拦截器
instance.interceptors.request.use(
  (config) => {
    const token = Cookies.get('userToken');
    if (token != undefined) {
      config.headers['Authorization'] = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

instance.interceptors.response.use(
  (response) => {
    if (response.data.code == '10002') {
      // 购买不够跳转 plan
      const timer = setTimeout(() => {
        message.warning(response.data.message);
        location.href = process.env.NEXT_APP_WEB_HOST + '/plan';
        clearTimeout(timer);
      }, 2000);
    } else if (response.data.code == '40001' && !isRedirecting) {
      // token 过期
      isRedirecting = true;
      message.warning(response.data.message);
      const timer = setTimeout(() => {
        isRedirecting = false;
        clearTimeout(timer);
        // location.href = process.env.NEXT_APP_WEB_HOST + '/login';
      }, 2000);
    }

    return response;
  },
  (error) => {
    return Promise.reject(error);
  },
);

export default instance;
